import { IndexedDBManager, type BaseRecord } from '@wyfe/h5-idb-sdk'
import type { City } from '@/hooks/useDB/city/types/city'

export interface CityRecord extends City, BaseRecord {
  firstLetter: string
  /** 0: 热门城市 hotAreas, 1: 已开通城市 openAreas */
  type: number
}

/** 城市业务数据（从 JSON/API 解析后的结构） */
export interface CityData {
  hotAreas: City[]
  openAreas: City[]
  lastestChangeTime: number
}

const CITY_DB_NAME = 'CityDatabase'
const CITY_STORE_NAME = 'cities'

// ─── 创建数据库管理器（直接实例化，不需要继承）───
export const db = new IndexedDBManager({
  name: CITY_DB_NAME,
  stores: { [CITY_STORE_NAME]: '++id, code, name, firstLetter, spell, type' },
  logLevel: 'info',
})

// ─── 获取 cities store（db.open() 之后才能调用）───
export const getCitiesStore = () => db.getStore<CityRecord>(CITY_STORE_NAME)

// ─── 数据转换工具 ───

/** 从原始数据解析出城市业务数据 */
export const parseCityData = (raw: any): CityData | null => {
  if (!raw?.hotAreas && !raw?.openAreas) return null
  return {
    hotAreas: raw.hotAreas || [],
    openAreas: raw.openAreas || [],
    lastestChangeTime: raw.lastestChangeTime || Date.now(),
  }
}

/** 校验单条城市记录是否包含必填字段 */
const isValidCity = (c: City): boolean => !!c.code && !!c.name && !!c.spell

/** 将业务数据转换为 DB 记录数组（过滤掉缺少必填字段的无效记录） */
export const toCityRecords = (data: CityData): CityRecord[] => {
  const hotValid = data.hotAreas.filter(isValidCity)
  const openValid = data.openAreas.filter(isValidCity)

  const droppedHot = data.hotAreas.length - hotValid.length
  const droppedOpen = data.openAreas.length - openValid.length
  if (droppedHot > 0 || droppedOpen > 0) {
    console.warn(`[cityDB] 过滤无效城市记录: hotAreas ${droppedHot}/${data.hotAreas.length}, openAreas ${droppedOpen}/${data.openAreas.length}`)
  }

  return [
    ...hotValid.map((c) => ({ ...c, firstLetter: c.spell.charAt(0).toUpperCase(), type: 0 })),
    ...openValid.map((c) => ({ ...c, firstLetter: c.spell.charAt(0).toUpperCase(), type: 1 })),
  ]
}

// ─── CRUD 函数 ───

/** 按类型获取城市数据（type: 0=热门城市, 1=已开通城市） */
export const getCitiesByType = async (type: number): Promise<CityRecord[]> => {
  const store = getCitiesStore()
  const t = performance.now()
  const result = await store.getWhere('type', type)
  console.log(`[cityDB] getCitiesByType(${type}) 耗时 ${(performance.now() - t).toFixed(2)}ms，共 ${result.length} 条`)
  return result
}

/** 根据城市名称模糊查询已开通城市 */
export const searchCitiesByName = async (keyword: string): Promise<CityRecord[]> => {
  const store = getCitiesStore()
  return await store.filter((city) => city.type === 1 && city.name.includes(keyword))
}

/** 获取当前数据版本号（存储在 localStorage，v1.0.4 SDK 已移除内置版本管理） */
export const getCityDataVersion = (): string | undefined => {
  return localStorage.getItem(`${CITY_DB_NAME}_version`) || undefined
}

/**
 * 版本比对 + 条件写入城市数据（单事务，自动回滚）
 * - 版本一致 → 跳过
 * - 版本不一致 → replaceAll 全量替换
 * - replaceAll 内部已有备份兜底，失败自动恢复
 * - 写入前校验必填字段（code/name/spell），过滤无效记录
 */
export const saveAllCitiesToDB = async (raw: { hotAreas?: City[]; openAreas?: City[] }, version: string): Promise<'updated' | 'skipped'> => {
  // 版本比对
  const currentVersion = localStorage.getItem(`${CITY_DB_NAME}_version`)
  if (currentVersion === version) {
    console.log(`[cityDB] 版本一致 (${version})，跳过写入`)
    return 'skipped'
  }

  const data = parseCityData(raw)
  if (!data) return 'skipped'

  const records = toCityRecords(data)

  // 校验后无有效记录，拒绝写入，保护已有数据
  if (records.length === 0) {
    console.warn('[cityDB] saveAllCitiesToDB 跳过：校验后无有效城市记录，保护已有数据')
    return 'skipped'
  }

  const store = getCitiesStore()

  const t = performance.now()
  await store.replaceAll(records)
  localStorage.setItem(`${CITY_DB_NAME}_version`, version)
  console.log(
    `[cityDB] saveAllCitiesToDB updated (${version}), 耗时 ${(performance.now() - t).toFixed(2)}ms，` +
      `hotAreas: ${data.hotAreas.length}, openAreas: ${data.openAreas.length}`,
  )
  return 'updated'
}
